/**
 * Split a VAT-INCLUSIVE amount into net + VAT.
 *
 * Plan prices are shown "VAT included" and Stripe charges exactly that gross
 * amount (no tax lines are added at checkout), so the invoice preview must
 * extract VAT from the gross instead of adding it on top. Same arithmetic as
 * the fiscal invoice (`packages/billing/src/invoice-service.ts`,
 * createSubscriptionInvoice): total = gross, net = total / (1 + rate), VAT =
 * total − net, each rounded to cents.
 */
export interface VatInclusiveSplit {
  /** What is charged — equals the input, rounded to cents. */
  total: number;
  /** Amount before VAT. */
  net: number;
  /** VAT contained in `total`. 0 when the rate is 0 (reverse charge, export). */
  vat: number;
}

const cents = (n: number) => Math.round(n * 100) / 100;

export function splitVatInclusive(gross: number, ratePct: number): VatInclusiveSplit {
  const total = cents(gross);
  if (!(ratePct > 0)) return { total, net: total, vat: 0 };
  const net = cents(total / (1 + ratePct / 100));
  return { total, net, vat: cents(total - net) };
}
